import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideHandshake,
  lucideRotateCw,
  lucideTriangleAlert,
  lucideUserCheck,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmAlertDialogImports } from '@spartan-ng/helm/alert-dialog';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { Observable } from 'rxjs';
import {
  PartnerAccessOverview,
  PartnerAccessService,
} from '../../../services/partner-access.service';
import { PartnerActionLogComponent } from './components/partner-action-log/partner-action-log.component';

/**
 * Settings → Deine Agentur: which agency works in the account, requests to answer,
 * ending the access, and what the agency changed. One agency at a time.
 */
@Component({
  selector: 'app-partner-access-settings',
  standalone: true,
  imports: [
    DatePipe,
    NgIcon,
    HlmAlertImports,
    HlmAlertDialogImports,
    HlmButtonImports,
    HlmCardImports,
    HlmItemImports,
    HlmSkeletonImports,
    HlmSpinnerImports,
    HlmTypographyImports,
    PartnerActionLogComponent,
  ],
  providers: [
    provideIcons({ lucideHandshake, lucideRotateCw, lucideTriangleAlert, lucideUserCheck }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './partner-access-settings.component.html',
})
export class PartnerAccessSettingsComponent {
  private readonly service = inject(PartnerAccessService);

  readonly overview = signal<PartnerAccessOverview | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  /** What is being saved: a request id with its answer, or 'revoke'. */
  readonly busy = signal<string | null>(null);
  readonly confirmRevoke = signal(false);

  constructor() {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service.overview().subscribe({
      next: (overview) => {
        this.overview.set(overview);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Die Angaben zu deiner Agentur konnten nicht geladen werden.');
        this.loading.set(false);
      },
    });
  }

  answer(requestId: number, answer: 'approve' | 'reject'): void {
    this.run(`${requestId}-${answer}`, this.service.answer(requestId, answer));
  }

  onRevokeDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed') this.confirmRevoke.set(false);
  }

  revoke(): void {
    this.confirmRevoke.set(false);
    this.run('revoke', this.service.revoke());
  }

  private run(key: string, request: Observable<PartnerAccessOverview>): void {
    if (this.busy()) return;
    this.busy.set(key);
    this.actionError.set(null);
    request.subscribe({
      next: (overview) => {
        this.overview.set(overview);
        this.busy.set(null);
      },
      error: (err) => {
        this.busy.set(null);
        this.actionError.set(
          err.status === 409
            ? err.error?.detail
            : 'Das hat nicht geklappt. Bitte versuche es erneut.',
        );
      },
    });
  }
}
